import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Input,
  Select,
  Button,
  Space,
  Tag,
  message,
  Dropdown,
  Menu,
  Modal,
  DatePicker,
  Row,
  Col,
  Statistic,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoreOutlined,
  DollarOutlined,
  FileTextOutlined,
  HistoryOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import InvoiceDetailModal from './InvoiceDetailModal';
import SubscriptionInvoiceCorrectionModal from './SubscriptionInvoiceCorrectionModal';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

const { RangePicker } = DatePicker;
const { Option } = Select;

dayjs.locale('fr');

interface Invoice {
  id: number;
  invoice_number: string;
  company_id: number;
  company_name: string;
  company_email: string;
  plan_name: string;
  invoice_date: string;
  due_date: string;
  period_start: string;
  period_end: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  currency: string;
  status: 'draft' | 'issued' | 'paid' | 'overdue' | 'cancelled' | 'refunded';
  payment_status: 'unpaid' | 'partial' | 'paid' | 'refunded';
  paid_amount?: number;
  payment_date?: string;
  payment_method?: string;
  metadata?: unknown;
}

const parseInvoiceMetadata = (metadata: unknown): Record<string, any> => {
  if (metadata && typeof metadata === 'object') return metadata as Record<string, any>;
  if (typeof metadata !== 'string') return {};
  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_error) {
    return {};
  }
};

const isHistoricalRegularizationInvoice = (invoice: Invoice) => {
  const metadata = parseInvoiceMetadata(invoice.metadata);
  return Boolean(
    metadata.historical_regularization ||
      metadata.generated_from === 'historical_regularization' ||
      metadata.generated_from === 'historical_regularization_correction',
  );
};

interface InvoiceListProps {
  companyId?: number; // Si fourni, filtre par entreprise
}

const InvoiceList: React.FC<InvoiceListProps> = ({ companyId }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [correctionInvoice, setCorrectionInvoice] = useState<Invoice | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Filtres
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Statistiques rapides
  const [statistics, setStatistics] = useState({
    total_billed: 0,
    total_paid: 0,
    total_outstanding: 0,
  });

  // Fonctions utilitaires pour formater les montants avec séparateurs de milliers
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    const formatted = formatAmount(amount);
    return `${formatted} XOF`;
  };

  // Charger les factures
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        limit: pagination.pageSize.toString(),
        ...(searchText && { search: searchText }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(paymentStatusFilter !== 'all' && { payment_status: paymentStatusFilter }),
        ...(companyId && { company_id: companyId.toString() }),
        ...(dateRange && {
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
        }),
      });
      const statisticsParams = new URLSearchParams({
        ...(companyId && { company_id: companyId.toString() }),
        ...(dateRange && {
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
        }),
      });
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      };

      const [response, statisticsResponse] = await Promise.all([
        fetch(`/api/subscription/invoices?${params}`, { headers }),
        fetch(`/api/subscription/invoices/statistics?${statisticsParams}`, { headers }),
      ]);

      const [data, statisticsData] = await Promise.all([
        response.json(),
        statisticsResponse.json(),
      ]);

      if (data.success) {
        setInvoices(data.data.invoices);
        setPagination({
          ...pagination,
          total: data.data.pagination.total,
        });

        if (statisticsData.success) {
          const totals = statisticsData.data?.general_statistics || {};
          setStatistics({
            total_billed: Number(totals.total_billed || 0),
            total_paid: Number(totals.total_paid || 0),
            total_outstanding: Number(totals.total_outstanding || 0),
          });
        }
      } else {
        message.error('Erreur lors du chargement des factures');
      }
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchText, statusFilter, paymentStatusFilter, dateRange, companyId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Télécharger le PDF d'une facture
  const handleDownloadPDF = async (invoiceId: number, invoiceNumber: string) => {
    setPdfLoading(true);
    try {
      const fileName = `Facture-${invoiceNumber}.pdf`;

      // Le serveur charge la facture officielle et prépare son QR.
      const pdfResponse = await fetch('/api/subscription/invoices/generate-pdf-react', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          invoiceId,
        }),
      });

      if (!pdfResponse.ok) {
        const errorData = await pdfResponse.json();
        throw new Error(errorData.error || 'Erreur lors de la génération du PDF');
      }

      // Télécharger le PDF (PATTERN EXACT DES VENTES)
      const blob = await pdfResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ [handleDownloadPDF] PDF généré avec succès');
      message.success('Facture téléchargée avec succès');
    } catch (error: any) {
      console.error('❌ [handleDownloadPDF] ERREUR:', error);
      console.error('❌ [handleDownloadPDF] Stack:', error.stack);
      message.error(error.message || 'Erreur lors de la génération du PDF');
    } finally {
      console.log('🏁 [handleDownloadPDF] Fin, setPdfLoading(false)');
      setPdfLoading(false);
    }
  };

  // Marquer comme payée
  const handleMarkAsPaid = async (invoice: Invoice) => {
    Modal.confirm({
      title: 'Marquer comme payée',
      content: `Êtes-vous sûr de vouloir marquer la facture ${invoice.invoice_number} comme payée ?`,
      okText: 'Oui, marquer comme payée',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const response = await fetch(`/api/subscription/invoices/${invoice.id}/mark-paid`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              payment_date: new Date().toISOString(),
              payment_method: 'manual',
              notes: 'Marquée comme payée manuellement',
            }),
          });

          const data = await response.json();

          if (data.success) {
            message.success(data.message || 'Facture marquée comme payée');
            fetchInvoices();
          } else {
            message.error(data.message || 'Erreur lors de la mise à jour');
          }
        } catch (error) {
          console.error('Erreur:', error);
          message.error('Erreur de connexion');
        }
      },
    });
  };

  // Annuler une facture
  const handleCancelInvoice = async (invoice: Invoice) => {
    Modal.confirm({
      title: 'Annuler la facture',
      content: `Êtes-vous sûr de vouloir annuler la facture ${invoice.invoice_number} ?`,
      okText: 'Oui, annuler',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const response = await fetch(`/api/subscription/invoices/${invoice.id}/cancel`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              reason: 'Annulation manuelle par l\'administrateur',
            }),
          });

          const data = await response.json();

          if (data.success) {
            message.success('Facture annulée');
            fetchInvoices();
          } else {
            message.error(data.message || 'Erreur lors de l\'annulation');
          }
        } catch (error) {
          console.error('Erreur:', error);
          message.error('Erreur de connexion');
        }
      },
    });
  };

  // Menu d'actions
  const getActionMenu = (invoice: Invoice) => (
    <Menu>
      <Menu.Item
        key="view"
        icon={<EyeOutlined />}
        onClick={() => {
          setSelectedInvoice(invoice);
          setDetailModalVisible(true);
        }}
      >
        Voir les détails
      </Menu.Item>

      <Menu.Item
        key="download"
        icon={<DownloadOutlined />}
        onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}
        disabled={pdfLoading}
      >
        {pdfLoading ? 'Génération PDF...' : 'Télécharger PDF'}
      </Menu.Item>

      {isHistoricalRegularizationInvoice(invoice) &&
        !['cancelled', 'refunded'].includes(invoice.status) && (
          <Menu.Item
            key="correct-regularization"
            icon={<HistoryOutlined />}
            onClick={() => setCorrectionInvoice(invoice)}
          >
            Corriger la régularisation
          </Menu.Item>
        )}

      {!['paid', 'refunded'].includes(invoice.payment_status) &&
        !['cancelled', 'refunded'].includes(invoice.status) && (
        <Menu.Item
          key="mark-paid"
          icon={<CheckCircleOutlined />}
          onClick={() => handleMarkAsPaid(invoice)}
        >
          Marquer comme payée
        </Menu.Item>
      )}

      {invoice.payment_status === 'unpaid' &&
        Number(invoice.paid_amount || 0) === 0 &&
        !['cancelled', 'paid', 'refunded'].includes(invoice.status) && (
        <Menu.Item
          key="cancel"
          icon={<CloseCircleOutlined />}
          danger
          onClick={() => handleCancelInvoice(invoice)}
        >
          Annuler la facture
        </Menu.Item>
      )}
    </Menu>
  );

  // Colonnes du tableau
  const columns: ColumnsType<Invoice> = [
    {
      title: 'N° Facture',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      width: 150,
      render: (text: string) => (
        <span className="font-mono font-semibold text-blue-600">{text}</span>
      ),
    },
    {
      title: 'Client',
      key: 'client',
      width: 200,
      render: (_, record) => (
        <div>
          <div className="font-semibold">{record.company_name}</div>
          <div className="text-xs text-gray-500">{record.company_email}</div>
        </div>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan_name',
      key: 'plan_name',
      width: 120,
      render: (text: string) => (
        <Tag color="blue">{text}</Tag>
      ),
    },
    {
      title: 'Date',
      key: 'dates',
      width: 160,
      render: (_, record) => (
        <div>
          <div className="text-sm">
            <strong>Émise:</strong> {dayjs(record.invoice_date).format('DD/MM/YYYY')}
          </div>
          <div className="text-sm text-gray-500">
            <strong>Échéance:</strong> {dayjs(record.due_date).format('DD/MM/YYYY')}
          </div>
        </div>
      ),
    },
    {
      title: 'Montant',
      key: 'amount',
      width: 140,
      align: 'right',
      render: (_, record) => (
        <div>
          <div className="text-lg font-bold">
            {formatAmount(record.total_amount)} {record.currency}
          </div>
          {record.tax_amount > 0 && (
            <div className="text-xs text-gray-500">
              dont TVA: {formatAmount(record.tax_amount)} {record.currency}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Statut',
      key: 'status',
      width: 140,
      render: (_, record) => (
        <InvoiceStatusBadge
          status={record.status}
          paymentStatus={record.payment_status}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Dropdown overlay={getActionMenu(record)} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      {/* Statistiques rapides */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Total Facturé"
              value={statistics.total_billed || 0}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Total Payé"
              value={statistics.total_paid || 0}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a', fontSize: '20px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="En Attente"
              value={statistics.total_outstanding || 0}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#faad14', fontSize: '20px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Barre de filtres */}
      <Card className="mb-4">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Rechercher..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={5}>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              className="w-full"
              placeholder="Statut"
            >
              <Option value="all">Tous les statuts</Option>
              <Option value="draft">Brouillon</Option>
              <Option value="issued">Émise</Option>
              <Option value="paid">Payée</Option>
              <Option value="overdue">En retard</Option>
              <Option value="cancelled">Annulée</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={5}>
            <Select
              value={paymentStatusFilter}
              onChange={setPaymentStatusFilter}
              className="w-full"
              placeholder="Paiement"
            >
              <Option value="all">Tous les paiements</Option>
              <Option value="unpaid">Non payée</Option>
              <Option value="partial">Partielle</Option>
              <Option value="paid">Payée</Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              format="DD/MM/YYYY"
              className="w-full"
              placeholder={['Date début', 'Date fin']}
            />
          </Col>

          <Col xs={24} sm={24} md={2}>
            <Tooltip title="Actualiser">
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchInvoices}
                loading={loading}
                block
              />
            </Tooltip>
          </Col>
        </Row>
      </Card>

      {/* Tableau des factures */}
      <Card>
        <Table
          columns={columns}
          dataSource={invoices}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} factures`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize: pageSize || 20 });
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Modal de détail */}
      <InvoiceDetailModal
        visible={detailModalVisible}
        invoice={selectedInvoice}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedInvoice(null);
        }}
        onRefresh={fetchInvoices}
      />

      <SubscriptionInvoiceCorrectionModal
        open={Boolean(correctionInvoice)}
        invoice={correctionInvoice}
        onClose={() => setCorrectionInvoice(null)}
        onSuccess={fetchInvoices}
      />
    </div>
  );
};

export default InvoiceList;
